import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { useAlertBlockedNotice } from '../../hooks/assistant/useAlertBlockedNotice';
import { useOpenAlertInAssistant } from '../../hooks/assistant/useOpenAlertInAssistant';
import { useMarkWorkItemsRead } from '../../hooks/mutations/useMarkWorkItemsRead';
import { useUpdateWorkItem } from '../../hooks/mutations/useUpdateWorkItem';
import {
  ARCHIVED_WORK_ITEM_FILTERS,
  UNARCHIVED_WORK_ITEM_FILTERS,
  useWorkItems,
  WORK_ITEM_ALERTS_PAGE_SIZE,
} from '../../hooks/queries/useWorkItems';
import { useWorkItemStatusCounts } from '../../hooks/queries/useWorkItemStatusCounts';
import { useFeatureGate } from '../../hooks/useFeatureGates';
import { useWorkItemNeedAttentionFloor } from '../../hooks/useWorkItemNeedAttentionFloor';
import { useAssistantTurnStatus } from '../../providers/AssistantTurnStatusProvider';
import { getWorkItemSectionCounts, groupWorkItemsBySection } from '../../utils/alerts';
import { buildAlertsCenterView } from '../../utils/alertsCenter';
import { getWorkItemAlertRowAction } from '../../utils/workItemAlertAction';
import { isWorkItemUnread, workItemToListedAlert } from '../../utils/workItemAlertRow';
import { useAlertRowAction } from '../alerts/useAlertRowAction';

import AlertsCenterBase from './AlertsCenterBase';

import type { ListedAlert } from '../../utils/alertRow';
import type { IAlertsCenterView } from '../../utils/alertsCenter';
import type { WorkItemDetailsFragment } from '../alerts/__generated__/workItemFragment.gql';

/** Rows per request, per read. The live read pages both of its sections at once. */
const PAGE_SIZE = WORK_ITEM_ALERTS_PAGE_SIZE;

/** Both reads take their filters from the hook that owns them, alongside its page size. */
const LIVE_FILTERS = UNARCHIVED_WORK_ITEM_FILTERS;
const HISTORY_FILTERS = ARCHIVED_WORK_ITEM_FILTERS;

export interface IAlertsCenterProps {
  /** At `xl+` collapses the column back to the assistant; below it, leaves the route. */
  onClose: () => void;
  className?: string;
}

/**
 * The Alerts Center, wired to `work_items` and rendering `AlertsCenterBase`.
 *
 * Two reads, not three: `getWorkItems` deliberately offers no status filter
 * (status is derived per viewer and clock), so the live read takes everything
 * unarchived and splits it client-side by the server-derived `status` into
 * need-attention and pending, while history pages independently. One
 * consequence, handled below: a `created_at`-ordered page can be all PENDING
 * while ACTIVE rows sit past the page boundary, so when the counts say active
 * work exists and the bucket is empty, the next page is fetched automatically
 * and the section stays rendered with its server count in the meantime —
 * otherwise the pip would point at rows the Center never shows.
 *
 * Click routing goes through `getWorkItemAlertRowAction` on the original
 * fragment row (CONFIG routes on the relation) via the hook's `runAction`
 * seam — the callout router is never invoked on this path, and it answers
 * `null` for adapted rows in any case, since they carry no subtype. A row with
 * no fix of its own opens in the assistant thread instead, which is also what
 * gives the column back to the conversation.
 */
const AlertsCenter = ({ onClose, className }: IAlertsCenterProps) => {
  const isAlertsCenterEnabled = useFeatureGate(FeatureGate.AlertsCenter);
  const { markRead } = useMarkWorkItemsRead();
  const { archiveWorkItem } = useUpdateWorkItem();
  const { runAction, alertActionSlot } = useAlertRowAction();
  const openAlertInAssistant = useOpenAlertInAssistant();
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const { isTurnInFlight } = useAssistantTurnStatus();
  const { blockedNoticeAlertId, showBlockedNotice } = useAlertBlockedNotice(isTurnInFlight);

  const skip = !isAlertsCenterEnabled;
  const live = useWorkItems({ filters: LIVE_FILTERS, pageSize: PAGE_SIZE, skip });
  const history = useWorkItems({ filters: HISTORY_FILTERS, pageSize: PAGE_SIZE, skip });
  const { counts, loading: countsLoading, refetch: refetchCounts } = useWorkItemStatusCounts({ skip });

  // The non-blocking read fails silently on screen, so this is the only trace
  // it leaves.
  React.useEffect(() => {
    if (history.error) {
      logger.error('Failed to load the Alerts Center history section', { history: history.error });
    }
  }, [history.error]);

  // Status is derived from the request clock, so cached pages go stale by time
  // alone — a due date crossing now moves the polled counts (the sidebar polls
  // the shared entry) while the rows keep yesterday's classification, and the
  // pip ends up pointing at a section this list never shows. Refetching when a
  // status bucket transitions resyncs the rows in lockstep. `unread` is
  // deliberately outside the key: mark-read moves it on every row click, and a
  // list refetch there would blank the loaded pages the narrowed cache
  // eviction exists to preserve.
  const statusBucketsKey = `${counts.active}:${counts.pending}:${counts.archived}`;
  const lastBucketsRef = React.useRef<string | null>(null);
  const { refetch: refetchLive } = live;
  const { refetch: refetchHistory } = history;
  React.useEffect(() => {
    if (countsLoading) {
      return;
    }
    if (lastBucketsRef.current === null || lastBucketsRef.current === statusBucketsKey) {
      lastBucketsRef.current = statusBucketsKey;
      return;
    }
    lastBucketsRef.current = statusBucketsKey;
    void refetchLive();
    void refetchHistory();
  }, [countsLoading, statusBucketsKey, refetchLive, refetchHistory]);

  const grouped = React.useMemo(() => groupWorkItemsBySection(live.workItems), [live.workItems]);

  // `countsLoading` is deliberately not passed: this surface resyncs on counts
  // transitions above, so the floor may chase from the first counted read.
  const { needAttentionMissing } = useWorkItemNeedAttentionFloor({
    activeCount: counts.active,
    needAttentionCount: grouped.needAttention.length,
    live,
  });

  const byId = React.useMemo(() => {
    const map = new Map<string, WorkItemDetailsFragment>();
    for (const workItem of [...live.workItems, ...history.workItems]) {
      map.set(workItem.id, workItem);
    }
    return map;
  }, [live.workItems, history.workItems]);

  const handleSelect = React.useCallback(
    (alert: ListedAlert) => {
      // Mid-turn, every row is locked — see `AssistantAlertCarousel` for why
      // the block covers action rows too. The click is refused whole, so the
      // row is not marked read either.
      if (isTurnInFlight) {
        showBlockedNotice(alert.id);
        return;
      }

      void markRead([alert.id]);

      // An alert the user can act on sends them at the fix instead of into the
      // transcript.
      const workItem = byId.get(alert.id);
      if (workItem && runAction(getWorkItemAlertRowAction(workItem))) {
        return;
      }

      // Read off the item rather than the row: `markRead` above has not landed
      // yet, and the transcript needs to know whether this was new to the user.
      if (openAlertInAssistant(alert.id, workItem !== undefined && isWorkItemUnread(workItem))) {
        return;
      }

      // No assistant to open it in — mark the row instead of stranding the click.
      setSelectedId(alert.id);
    },
    [isTurnInFlight, showBlockedNotice, byId, markRead, runAction, openAlertInAssistant]
  );

  const handleDismiss = React.useCallback(
    (alertId: string) => {
      setSelectedId((current) => (current === alertId ? null : current));
      void archiveWorkItem(alertId);
    },
    [archiveWorkItem]
  );

  const handleRetry = React.useCallback(() => {
    void live.refetch();
    void history.refetch();
    void refetchCounts();
  }, [live, history, refetchCounts]);

  const sectionCounts = getWorkItemSectionCounts(counts);
  const livePager = { hasMore: live.hasMore, isLoadingMore: live.isLoadingMore, onLoadMore: live.loadMore };
  const chasingNeedAttention = needAttentionMissing && live.hasMore;
  // One server list feeds two sections, so there is one pager. It sits on the
  // last non-empty live section — pending when it has rows — because a page
  // fetched from either button appends to both groups, and rows materializing
  // above the pressed button reads as the list jumping. While the auto-fetch
  // floor is chasing active rows, its synthetic section below holds the pager
  // instead.
  const pagerOnPending = grouped.pending.length > 0 && !chasingNeedAttention;

  const view = buildAlertsCenterView({
    sections: {
      needAttention: {
        rows: grouped.needAttention.map(workItemToListedAlert),
        count: sectionCounts.needAttention,
        ...(pagerOnPending ? {} : livePager),
      },
      pending: {
        rows: grouped.pending.map(workItemToListedAlert),
        count: sectionCounts.pending,
        ...(pagerOnPending ? livePager : {}),
      },
      history: {
        rows: history.workItems.map(workItemToListedAlert),
        count: sectionCounts.history,
        hasMore: history.hasMore,
        isLoadingMore: history.isLoadingMore,
        onLoadMore: history.loadMore,
      },
    },
    isLoading: live.loading || history.loading,
    hasBlockingError: live.error !== undefined,
  });

  // The floor's visible half: while the auto-fetch chases the counted-but-
  // unfetched active rows, render the section header over its own pager rather
  // than letting `buildAlertsCenterView` drop the empty group.
  const sections: IAlertsCenterView['sections'] =
    view.status === 'ready' && chasingNeedAttention
      ? [
          { section: 'needAttention', rows: [], count: sectionCounts.needAttention, ...livePager, isLoadingMore: true },
          ...view.sections,
        ]
      : view.sections;

  return (
    <AlertsCenterBase
      sections={sections}
      status={view.status}
      onClose={onClose}
      selectedId={selectedId}
      blockedNoticeAlertId={blockedNoticeAlertId}
      onSelect={handleSelect}
      onDismiss={handleDismiss}
      onRetry={handleRetry}
      actionSlot={alertActionSlot}
      className={className}
    />
  );
};

export default AlertsCenter;
